/**
 * Obligation sources — V98-4-1 / D-98-3 (UX-3); PRD G-4; research §7.
 *
 * The AI Regulatory program's legislation / case-law toggle is a VERSIONED
 * Legal decision (migration 237 `obligation_source_decisions`), never a
 * setting:
 *
 *   proposals  onboarding answers and admin "propose a change" rows. They
 *              never change anything on their own; Legal folds them into a
 *              draft (accepted) or declines them with a reason. A newer
 *              onboarding answer for the same system supersedes the older.
 *   decisions  v1, v2, … — Legal's draft, then recorded. Recording replaces
 *              the previous version (kept in history, immutable). One open
 *              draft and one current version per workspace.
 *   ceiling    the org's `organizations.policy_source`. It is displayed as a
 *              locked limit with who set it and when; a draft, a recording
 *              or a proposal that turns legislation or case law on above a
 *              `frameworks_only` ceiling is rejected (`above_ceiling`), and
 *              the table's CHECK makes that true in the database too.
 *
 * Authorization lives in the route: the legal reviewer (a current
 * `legal_reviewer` assignment on the AI Regulatory program) drafts, records
 * and declines; owner / admin propose. Every id is resolved inside the
 * caller's workspace. Every change appends an `ai_program_events` row in the
 * same batch.
 *
 * Privilege: a decision's rationale is Legal's analysis. `projectDecision`
 * strips it for anyone but the legal reviewer unless Legal shared it with
 * the workspace (visibility workspace / external and not privileged).
 */
import { generateRandomId } from '../utils/audit-crypto.js';
import { AI_REGULATORY_PROGRAM, getActiveProfile } from './certification-service.js';
import { activeMemberIds } from './data-plane-router.js';
export const RATIONALE_VISIBILITIES = ['legal_only', 'workspace', 'external'];
/** Re-review triggers Legal can attach to a version. */
export const REREVIEW_TRIGGERS = ['new_system', 'law_amendment', 'new_market', 'case_law', 'date'];
export class ObligationSourceError extends Error {
    code;
    detail;
    constructor(code, message, detail) {
        super(message);
        this.code = code;
        this.detail = detail;
        this.name = 'ObligationSourceError';
    }
}
const JURISDICTION_RE = /^[A-Z]{2,5}(?:-[A-Z0-9]{1,6}){0,3}$/;
const MAX_JURISDICTIONS = 60;
const text = (v, max = 2000) => {
    if (typeof v !== 'string')
        return null;
    const t = v.trim();
    return t ? t.slice(0, max) : null;
};
const parseList = (s) => {
    try {
        const v = JSON.parse(s ?? '[]');
        return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [];
    }
    catch {
        return [];
    }
};
const parseObj = (s) => {
    try {
        const v = JSON.parse(s ?? '{}');
        return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
    }
    catch {
        return {};
    }
};
export function normalizeJurisdictions(v) {
    if (!Array.isArray(v))
        throw new ObligationSourceError('invalid', 'jurisdictions must be a list of codes.');
    const out = [...new Set(v.map((x) => (typeof x === 'string' ? x.trim().toUpperCase() : '')))];
    if (out.some((j) => !JURISDICTION_RE.test(j)))
        throw new ObligationSourceError('invalid', 'Each jurisdiction is a code like EU, US-CO or US-NY-NYC.');
    if (out.length > MAX_JURISDICTIONS)
        throw new ObligationSourceError('invalid', `At most ${MAX_JURISDICTIONS} jurisdictions.`);
    return out.sort();
}
// ---- ceiling -----------------------------------------------------------------------------
/** The org ceiling for a workspace (no org → no limit). */
export async function getCeiling(db, workspaceId) {
    const r = await db.query(`SELECT o.organization_id, o.policy_source, o.policy_source_set_by, o.policy_source_set_at,
            COALESCE(u.display_name, u.email) AS set_by_name
       FROM workspaces w JOIN organizations o ON o.organization_id = w.organization_id
       LEFT JOIN users u ON u.user_id = o.policy_source_set_by
      WHERE w.workspace_id = ? LIMIT 1`, [workspaceId]);
    const o = r.results[0];
    const policy = o?.policy_source === 'frameworks_only' ? 'frameworks_only' : 'frameworks_and_legislation';
    return {
        organization_id: o?.organization_id ?? null, policy_source: policy, set_by: o?.policy_source_set_by ?? null,
        set_by_name: o?.set_by_name ?? null, set_at: o?.policy_source_set_at ?? null, allows_legislation: policy === 'frameworks_and_legislation',
    };
}
/** Throws `above_ceiling` when the change turns legislation or case law on above the ceiling. */
export function assertWithinCeiling(ceiling, change) {
    if (ceiling.allows_legislation)
        return;
    if (change.legislation_on || change.case_law_on) {
        throw new ObligationSourceError('above_ceiling', 'Your organization’s policy tracks frameworks only. AI legislation and case-law signals stay off until an org admin changes the policy.', { ceiling: ceiling.policy_source, set_by: ceiling.set_by, set_at: ceiling.set_at });
    }
}
// ---- reads -------------------------------------------------------------------------------
export async function requireAiProgram(db, workspaceId) {
    const p = await getActiveProfile(db, workspaceId, AI_REGULATORY_PROGRAM);
    if (!p)
        throw new ObligationSourceError('no_program', 'AI Regulatory is not set up in this workspace.');
    return { profile_id: p.profile_id };
}
async function one(db, sql, params) {
    return (await db.query(sql, params)).results[0] ?? null;
}
export async function getCurrentDecision(db, workspaceId) {
    return one(db, `SELECT * FROM obligation_source_decisions WHERE workspace_id = ? AND kind = 'decision' AND state = 'recorded' LIMIT 1`, [workspaceId]);
}
export async function getDraft(db, workspaceId) {
    return one(db, `SELECT * FROM obligation_source_decisions WHERE workspace_id = ? AND kind = 'decision' AND state = 'draft' LIMIT 1`, [workspaceId]);
}
export async function getObligationSourceRow(db, workspaceId, id) {
    return one(db, `SELECT * FROM obligation_source_decisions WHERE workspace_id = ? AND decision_id = ? LIMIT 1`, [workspaceId, id]);
}
export async function listDecisionHistory(db, workspaceId) {
    return (await db.query(`SELECT * FROM obligation_source_decisions WHERE workspace_id = ? AND kind = 'decision' ORDER BY version DESC`, [workspaceId])).results;
}
export async function listProposals(db, workspaceId, opts = {}) {
    return (await db.query(`SELECT * FROM obligation_source_decisions WHERE workspace_id = ? AND kind = 'proposal' ${opts.open ? `AND state = 'proposed'` : ''}
      ORDER BY proposed_at ASC, decision_id ASC`, [workspaceId])).results;
}
/** The working scope: the open draft, else the current version (null before v1). */
export async function getWorkingDecision(db, workspaceId) {
    return (await getDraft(db, workspaceId)) ?? (await getCurrentDecision(db, workspaceId));
}
/** What actually applies: the current recorded version, capped by today's ceiling. */
export function effectiveSources(current, ceiling) {
    const legislation = !!current?.legislation_on && ceiling.allows_legislation;
    return {
        frameworks: true, legislation, case_law: legislation && !!current?.case_law_on,
        jurisdictions: legislation ? parseList(current?.jurisdictions_json) : [], version: current?.version ?? null, ceiling: ceiling.policy_source,
    };
}
// ---- privilege projection ----------------------------------------------------------------
/** May this reader see Legal's rationale on a row with this visibility? */
export function rationaleVisibleTo(row, isLegal) {
    if (isLegal)
        return true;
    return row.privileged === 0 && (row.rationale_visibility === 'workspace' || row.rationale_visibility === 'external');
}
const bool = (n) => (n === null || n === undefined ? null : n === 1);
/** Field-level projection: the rationale leaves the server only for the legal reviewer (or when Legal shared it). */
export function projectDecision(row, isLegal) {
    const see = rationaleVisibleTo(row, isLegal);
    return {
        decision_id: row.decision_id, kind: row.kind, version: row.version, state: row.state, source: row.source,
        legislation_on: bool(row.legislation_on), case_law_on: bool(row.case_law_on), jurisdictions: parseList(row.jurisdictions_json),
        system_id: row.system_id, facts: parseObj(row.facts_json), note: row.note,
        rationale: see ? row.rationale : null, rationale_withheld: !see && row.rationale !== null,
        rationale_visibility: row.rationale_visibility, privileged: row.privileged === 1,
        review_by: row.review_by, triggers: parseList(row.triggers_json), accepted_proposal_ids: parseList(row.accepted_proposal_ids_json),
        ceiling: { policy_source: row.ceiling_policy_source, set_by: row.ceiling_set_by, set_at: row.ceiling_set_at },
        proposed_by: row.proposed_by, proposed_at: row.proposed_at, decided_by: row.decided_by, decided_at: row.decided_at,
        resolved_decision_id: row.resolved_decision_id, resolved_by: row.resolved_by, resolved_at: row.resolved_at,
        resolved_reason: row.resolved_reason, replaced_at: row.replaced_at, created_at: row.created_at, updated_at: row.updated_at,
    };
}
// ---- events ------------------------------------------------------------------------------
export function aiProgramEvent(ws, subject, event, actor, at, extra = {}) {
    return {
        sql: `INSERT INTO ai_program_events (event_id, workspace_id, subject_kind, subject_id, event, actor_user_id, reason, detail_json, at)
          VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        params: [(extra.newId ?? (() => generateRandomId('ape')))(), ws, subject.kind, subject.id, event, actor, extra.reason ?? null,
            extra.detail === undefined ? null : JSON.stringify(extra.detail), at],
    };
}
/** Events whose `reason` is the legal reviewer's own free text (V98-7-3 L2). */
const LEGAL_TEXT_EVENTS = new Set(['declined', 'fact_withdrawn', 'case_law_removed', 'privilege_revoked']);
/**
 * V98-7-3 M3 / L2: what a NON-Legal reader sees of the program log. Release
 * events keep only that a release happened (no release id, recipient, purpose,
 * determinations or revoke reason — the id unlocks the rationale export);
 * exports drop the release id they used; Legal's own free-text reasons
 * (decline, withdraw, remove, revoke) are withheld. Legal sees everything.
 */
export function projectAiProgramEvents(events, viewer) {
    if (viewer.legal)
        return events;
    return events.map((e) => {
        if (e.subject_kind === 'privilege_release')
            return { ...e, subject_id: null, reason: null, detail_json: null };
        if (e.event === 'determinations_exported' && e.detail_json) {
            try {
                const { release_id: _r, ...rest } = JSON.parse(e.detail_json);
                void _r;
                return { ...e, detail_json: JSON.stringify(rest) };
            }
            catch {
                return { ...e, detail_json: null };
            }
        }
        return LEGAL_TEXT_EVENTS.has(e.event) ? { ...e, reason: null } : e;
    });
}
export async function listAiProgramEvents(db, workspaceId, subject) {
    return (await db.query(subject
        ? `SELECT event_id, subject_kind, subject_id, event, actor_user_id, reason, detail_json, at FROM ai_program_events
          WHERE workspace_id = ? AND subject_kind = ? AND subject_id = ? ORDER BY at ASC, event_id ASC`
        : `SELECT event_id, subject_kind, subject_id, event, actor_user_id, reason, detail_json, at FROM ai_program_events
          WHERE workspace_id = ? ORDER BY at DESC, event_id DESC LIMIT 200`, subject ? [workspaceId, subject.kind, subject.id] : [workspaceId])).results;
}
/** Logged even when the change itself is refused (the ceiling is a control auditors test). */
async function logRejection(db, ws, actor, at, change, ceiling) {
    const ev = aiProgramEvent(ws, { kind: 'workspace', id: ws }, 'rejected_above_ceiling', actor, at, { reason: `Ceiling: ${ceiling.policy_source}`, detail: change });
    await db.execute(ev.sql, ev.params);
}
export async function createProposal(db, input) {
    const now = input.now ?? new Date();
    const at = now.toISOString();
    const ws = input.workspace_id;
    const note = text(input.note, 1000);
    if (!note)
        throw new ObligationSourceError('invalid', 'Say what should change.');
    const ceiling = await getCeiling(db, ws);
    const change = { legislation_on: input.legislation_on ?? null, case_law_on: input.case_law_on ?? null };
    if (change.case_law_on && change.legislation_on === false)
        throw new ObligationSourceError('invalid', 'Case-law signals need AI legislation on.');
    try {
        assertWithinCeiling(ceiling, change);
    }
    catch (err) {
        await logRejection(db, ws, input.actor_user_id, at, { kind: 'proposal', ...change }, ceiling);
        throw err;
    }
    const jurisdictions = input.jurisdictions ? normalizeJurisdictions(input.jurisdictions) : [];
    if (input.system_id) {
        const s = await db.query(`SELECT s.system_id FROM ai_systems s LEFT JOIN customer_profiles p ON p.profile_id = s.profile_id
        WHERE s.system_id = ? AND (s.workspace_id = ? OR (s.workspace_id IS NULL AND p.workspace_id = ?)) LIMIT 1`, [input.system_id, ws, ws]);
        if (!s.results[0])
            throw new ObligationSourceError('not_found', 'System not found.');
    }
    const program = await getActiveProfile(db, ws, AI_REGULATORY_PROGRAM);
    const id = (input.newId ?? (() => generateRandomId('osd')))();
    const statements = [];
    // A newer onboarding answer for the same system supersedes the open one (Legal decides on the latest).
    if (input.source === 'onboarding' && input.system_id) {
        const open = await db.query(`SELECT decision_id FROM obligation_source_decisions WHERE workspace_id = ? AND kind = 'proposal' AND source = 'onboarding'
          AND system_id = ? AND state = 'proposed'`, [ws, input.system_id]);
        for (const o of open.results) {
            statements.push({
                sql: `UPDATE obligation_source_decisions SET state = 'superseded', resolved_by = ?, resolved_at = ?, resolved_reason = ?, updated_at = ?
               WHERE decision_id = ? AND workspace_id = ? AND state = 'proposed'`,
                params: [input.actor_user_id, at, 'Superseded by newer onboarding answers', at, o.decision_id, ws],
            });
            statements.push(aiProgramEvent(ws, { kind: 'obligation_source_decision', id: o.decision_id }, 'superseded', input.actor_user_id, at, { reason: `By ${id}` }));
        }
    }
    statements.push({
        sql: `INSERT INTO obligation_source_decisions (decision_id, workspace_id, profile_id, kind, version, state, source, legislation_on, case_law_on,
            jurisdictions_json, facts_json, system_id, note, ceiling_policy_source, ceiling_set_by, ceiling_set_at, proposed_by, proposed_at,
            created_by, created_at, updated_at)
          VALUES (?, ?, ?, 'proposal', NULL, 'proposed', ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        params: [id, ws, program?.profile_id ?? null, input.source,
            change.legislation_on === null ? null : change.legislation_on ? 1 : 0, change.case_law_on === null ? null : change.case_law_on ? 1 : 0,
            JSON.stringify(jurisdictions), JSON.stringify(input.facts ?? {}), input.system_id ?? null, note,
            ceiling.policy_source, ceiling.set_by, ceiling.set_at, input.actor_user_id, at, input.actor_user_id, at, at],
    });
    statements.push(aiProgramEvent(ws, { kind: 'obligation_source_decision', id }, 'proposed', input.actor_user_id, at, { detail: { source: input.source } }));
    await db.batch(statements);
    return (await getObligationSourceRow(db, ws, id));
}
export async function declineProposal(db, input) {
    const at = (input.now ?? new Date()).toISOString();
    const reason = text(input.reason, 1000);
    if (!reason)
        throw new ObligationSourceError('invalid', 'Say why the proposal is declined.');
    const p = await getObligationSourceRow(db, input.workspace_id, input.proposal_id);
    if (!p || p.kind !== 'proposal')
        throw new ObligationSourceError('not_found', 'Proposal not found.');
    if (p.state !== 'proposed')
        throw new ObligationSourceError('conflict', `This proposal is already ${p.state}.`);
    await db.batch([
        {
            sql: `UPDATE obligation_source_decisions SET state = 'declined', resolved_by = ?, resolved_at = ?, resolved_reason = ?, updated_at = ?
             WHERE decision_id = ? AND workspace_id = ? AND state = 'proposed'`,
            params: [input.actor_user_id, at, reason, at, p.decision_id, input.workspace_id],
        },
        aiProgramEvent(input.workspace_id, { kind: 'obligation_source_decision', id: p.decision_id }, 'declined', input.actor_user_id, at, { reason }),
    ]);
    return (await getObligationSourceRow(db, input.workspace_id, p.decision_id));
}
/**
 * The open draft, created on first use as the next version (a copy of the
 * current version's sources, so "carried from vN" is the default).
 */
export async function ensureDraft(db, input) {
    const existing = await getDraft(db, input.workspace_id);
    if (existing)
        return existing;
    const { profile_id } = await requireAiProgram(db, input.workspace_id);
    const at = (input.now ?? new Date()).toISOString();
    const current = await getCurrentDecision(db, input.workspace_id);
    const ceiling = await getCeiling(db, input.workspace_id);
    const max = await db.query(`SELECT MAX(version) AS v FROM obligation_source_decisions WHERE workspace_id = ? AND kind = 'decision'`, [input.workspace_id]);
    const version = (max.results[0]?.v ?? 0) + 1;
    // Carried sources are capped by today's ceiling (a lowered ceiling switches legislation off in the draft).
    const legislation = current?.legislation_on === 1 && ceiling.allows_legislation ? 1 : 0;
    const caseLaw = legislation === 1 && current?.case_law_on === 1 ? 1 : 0;
    const id = (input.newId ?? (() => generateRandomId('osd')))();
    try {
        await db.batch([
            {
                sql: `INSERT INTO obligation_source_decisions (decision_id, workspace_id, profile_id, kind, version, state, source, legislation_on, case_law_on,
                jurisdictions_json, rationale_visibility, privileged, review_by, triggers_json, ceiling_policy_source, ceiling_set_by, ceiling_set_at,
                created_by, created_at, updated_at)
              VALUES (?, ?, ?, 'decision', ?, 'draft', 'legal', ?, ?, ?, 'legal_only', 1, ?, ?, ?, ?, ?, ?, ?, ?)`,
                params: [id, input.workspace_id, profile_id, version, legislation, caseLaw, current?.jurisdictions_json ?? '[]',
                    current?.review_by ?? null, current?.triggers_json ?? '[]', ceiling.policy_source, ceiling.set_by, ceiling.set_at,
                    input.actor_user_id, at, at],
            },
            aiProgramEvent(input.workspace_id, { kind: 'obligation_source_decision', id }, 'draft_created', input.actor_user_id, at, { detail: { version, carried_from: current?.version ?? null } }),
        ]);
    }
    catch (err) {
        // A concurrent first save created the draft (one open draft per workspace).
        const raced = await getDraft(db, input.workspace_id);
        if (raced)
            return raced;
        throw err;
    }
    return (await getDraft(db, input.workspace_id));
}
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
export async function saveDraft(db, input) {
    const now = input.now ?? new Date();
    const at = now.toISOString();
    const ws = input.workspace_id;
    const ceiling = await getCeiling(db, ws);
    const draft = await ensureDraft(db, { workspace_id: ws, actor_user_id: input.actor_user_id, now, newId: input.newId });
    const legislation = input.legislation_on ?? draft.legislation_on === 1;
    const caseLaw = input.case_law_on ?? (draft.case_law_on === 1 && legislation);
    if (caseLaw && !legislation)
        throw new ObligationSourceError('invalid', 'Case-law signals need AI legislation on.');
    try {
        assertWithinCeiling(ceiling, { legislation_on: legislation, case_law_on: caseLaw });
    }
    catch (err) {
        await logRejection(db, ws, input.actor_user_id, at, { kind: 'draft', version: draft.version, legislation_on: legislation, case_law_on: caseLaw }, ceiling);
        throw err;
    }
    const jurisdictions = input.jurisdictions !== undefined ? normalizeJurisdictions(input.jurisdictions) : parseList(draft.jurisdictions_json);
    let accepted = parseList(draft.accepted_proposal_ids_json);
    if (input.accept_proposal_ids) {
        const ids = [...new Set(input.accept_proposal_ids)];
        for (const pid of ids) {
            const p = await getObligationSourceRow(db, ws, pid);
            if (!p || p.kind !== 'proposal')
                throw new ObligationSourceError('not_found', 'Proposal not found.');
            if (p.state !== 'proposed')
                throw new ObligationSourceError('conflict', `Proposal ${pid} is already ${p.state}.`);
        }
        accepted = ids.sort();
    }
    const visibility = input.rationale_visibility ?? draft.rationale_visibility ?? 'legal_only';
    if (!RATIONALE_VISIBILITIES.includes(visibility))
        throw new ObligationSourceError('invalid', 'Unknown rationale visibility.');
    const reviewBy = input.review_by !== undefined ? input.review_by : draft.review_by;
    if (reviewBy && !DAY_RE.test(reviewBy))
        throw new ObligationSourceError('invalid', 'review_by is a date (YYYY-MM-DD).');
    const triggers = input.triggers !== undefined ? [...new Set(input.triggers)] : parseList(draft.triggers_json);
    if (triggers.some((t) => !REREVIEW_TRIGGERS.includes(t)))
        throw new ObligationSourceError('invalid', 'Unknown re-review trigger.');
    const rationale = input.rationale !== undefined ? text(input.rationale, 2000) : draft.rationale;
    await db.batch([
        {
            sql: `UPDATE obligation_source_decisions SET legislation_on = ?, case_law_on = ?, jurisdictions_json = ?, accepted_proposal_ids_json = ?,
              rationale = ?, rationale_visibility = ?, privileged = ?, review_by = ?, triggers_json = ?,
              ceiling_policy_source = ?, ceiling_set_by = ?, ceiling_set_at = ?, updated_at = ?
             WHERE decision_id = ? AND workspace_id = ? AND state = 'draft'`,
            params: [legislation ? 1 : 0, caseLaw ? 1 : 0, JSON.stringify(jurisdictions), JSON.stringify(accepted), rationale, visibility,
                visibility === 'legal_only' ? 1 : 0, reviewBy ?? null, JSON.stringify(triggers), ceiling.policy_source, ceiling.set_by, ceiling.set_at,
                at, draft.decision_id, ws],
        },
        aiProgramEvent(ws, { kind: 'obligation_source_decision', id: draft.decision_id }, 'draft_updated', input.actor_user_id, at, { detail: { version: draft.version, legislation_on: legislation, case_law_on: caseLaw, jurisdictions, accepted } }),
    ]);
    return (await getDraft(db, ws));
}
/** Record the open draft as the current version (Legal). The previous version is replaced, never edited. */
export async function recordDecision(db, input) {
    const now = input.now ?? new Date();
    const at = now.toISOString();
    const ws = input.workspace_id;
    // Fold the final fields in first (same validation, incl. the ceiling).
    const draft = await saveDraft(db, {
        workspace_id: ws, actor_user_id: input.actor_user_id, now,
        ...(input.rationale !== undefined ? { rationale: input.rationale } : {}),
        ...(input.rationale_visibility ? { rationale_visibility: input.rationale_visibility } : {}),
        ...(input.review_by !== undefined ? { review_by: input.review_by } : {}),
        ...(input.triggers ? { triggers: input.triggers } : {}),
    });
    if (!draft.rationale)
        throw new ObligationSourceError('invalid', 'A one-line rationale is required to record a decision.');
    const accepted = parseList(draft.accepted_proposal_ids_json);
    for (const pid of accepted) {
        const p = await getObligationSourceRow(db, ws, pid);
        if (!p || p.state !== 'proposed')
            throw new ObligationSourceError('conflict', `Proposal ${pid} is no longer open; update the draft.`);
    }
    const current = await getCurrentDecision(db, ws);
    const statements = [];
    if (current) {
        statements.push({
            sql: `UPDATE obligation_source_decisions SET state = 'replaced', replaced_by_decision_id = ?, replaced_at = ?, updated_at = ?
             WHERE decision_id = ? AND workspace_id = ? AND state = 'recorded'`,
            params: [draft.decision_id, at, at, current.decision_id, ws],
        });
        statements.push(aiProgramEvent(ws, { kind: 'obligation_source_decision', id: current.decision_id }, 'replaced', input.actor_user_id, at, { detail: { by_version: draft.version } }));
    }
    statements.push({
        sql: `UPDATE obligation_source_decisions SET state = 'recorded', decided_by = ?, decided_at = ?, updated_at = ?
           WHERE decision_id = ? AND workspace_id = ? AND state = 'draft'`,
        params: [input.actor_user_id, at, at, draft.decision_id, ws],
    });
    for (const pid of accepted) {
        statements.push({
            sql: `UPDATE obligation_source_decisions SET state = 'accepted', resolved_decision_id = ?, resolved_by = ?, resolved_at = ?, updated_at = ?
             WHERE decision_id = ? AND workspace_id = ? AND state = 'proposed'`,
            params: [draft.decision_id, input.actor_user_id, at, at, pid, ws],
        });
        statements.push(aiProgramEvent(ws, { kind: 'obligation_source_decision', id: pid }, 'accepted', input.actor_user_id, at, { detail: { version: draft.version } }));
    }
    if (input.extra)
        statements.push(...(await input.extra(draft, at)));
    statements.push(aiProgramEvent(ws, { kind: 'obligation_source_decision', id: draft.decision_id }, 'recorded', input.actor_user_id, at, { detail: { version: draft.version, replaced: current?.version ?? null, legislation_on: draft.legislation_on === 1, case_law_on: draft.case_law_on === 1,
            jurisdictions: parseList(draft.jurisdictions_json) } }));
    await db.batch(statements);
    return (await getObligationSourceRow(db, ws, draft.decision_id));
}
export async function getObligationSourcesView(db, workspaceId, reader) {
    const [ceiling, current, draft, proposals, history, legal] = await Promise.all([
        getCeiling(db, workspaceId), getCurrentDecision(db, workspaceId), getDraft(db, workspaceId),
        listProposals(db, workspaceId, { open: true }), listDecisionHistory(db, workspaceId), legalReviewers(db, workspaceId),
    ]);
    const p = (r) => projectDecision(r, reader.is_legal);
    const nameOf = async (u) => (await db.query(`SELECT COALESCE(display_name, email) AS n FROM users WHERE user_id = ? LIMIT 1`, [u])).results[0]?.n ?? null;
    const org = ceiling.organization_id
        ? (await db.query(`SELECT slug FROM organizations WHERE organization_id = ? LIMIT 1`, [ceiling.organization_id])).results[0] ?? null
        : null;
    const orgAdmin = !!reader.user_id && !!ceiling.organization_id && !!(await db.query(`SELECT 1 AS ok FROM org_memberships WHERE organization_id = ? AND user_id = ? AND role = 'organization_admin' AND removed_at IS NULL LIMIT 1`, [ceiling.organization_id, reader.user_id])).results[0];
    // Who an ask goes to: the org admin who set the policy when they are in this workspace, else an owner / admin.
    let contact = null;
    if (!ceiling.allows_legislation) {
        if (ceiling.set_by && (await db.query(`SELECT 1 AS ok FROM memberships WHERE workspace_id = ? AND user_id = ? AND removed_at IS NULL LIMIT 1`, [workspaceId, ceiling.set_by])).results[0])
            contact = ceiling.set_by;
        contact ??= (await db.query(`SELECT user_id FROM memberships WHERE workspace_id = ? AND role IN ('owner', 'admin') AND removed_at IS NULL
        ORDER BY CASE role WHEN 'owner' THEN 0 ELSE 1 END, joined_at ASC, user_id ASC LIMIT 1`, [workspaceId])).results[0]?.user_id ?? null;
    }
    return {
        ceiling, effective: effectiveSources(current, ceiling), current: current ? p(current) : null, draft: draft ? p(draft) : null,
        proposals: proposals.map(p), history: history.map(p), can_decide: reader.is_legal, can_propose: reader.is_admin,
        legal_reviewer: legal[0] ? { user_id: legal[0], name: await nameOf(legal[0]) } : null,
        org_slug: org?.slug ?? null, can_change_policy: orgAdmin,
        policy_contact: contact ? { user_id: contact, name: await nameOf(contact) } : null,
    };
}
// ---- who decides -------------------------------------------------------------------------
/** Current legal reviewers who are active members: the AI Regulatory program's first, then any other. */
export async function legalReviewers(db, workspaceId, now = new Date()) {
    const iso = now.toISOString();
    const aiReg = await getActiveProfile(db, workspaceId, AI_REGULATORY_PROGRAM);
    const r = await db.query(`SELECT a.user_id, a.subject_kind, a.subject_id FROM assignments a
      WHERE a.workspace_id = ? AND a.responsibility = 'legal_reviewer'
        AND a.effective_from <= ? AND (a.effective_to IS NULL OR a.effective_to > ?)
      ORDER BY a.effective_from ASC, a.user_id ASC`, [workspaceId, iso, iso]);
    // V98-7-5: active members only — memberships are control-plane, filtered here rather than JOINed.
    const members = await activeMemberIds(db, workspaceId);
    const current = r.results.filter((x) => members.has(x.user_id));
    const ordered = [
        ...current.filter((x) => aiReg && x.subject_kind === 'program' && x.subject_id === aiReg.profile_id),
        ...current,
    ].map((x) => x.user_id);
    return [...new Set(ordered)];
}
/**
 * The legal reviewer who receives a proposal: the program's legal reviewer,
 * else any current legal reviewer, else `fallbackUserId` (an owner / admin),
 * so a proposal is never unowned.
 */
export async function resolveLegalReviewer(db, workspaceId, fallbackUserId, now = new Date()) {
    return (await legalReviewers(db, workspaceId, now))[0] ?? fallbackUserId;
}
/** Does the user hold the deciding hat (legal reviewer on the AI Regulatory program)? */
export async function isAiLegalReviewer(db, workspaceId, userId, now = new Date()) {
    const aiReg = await getActiveProfile(db, workspaceId, AI_REGULATORY_PROGRAM);
    if (!aiReg)
        return false;
    const iso = now.toISOString();
    const r = await db.query(`SELECT 1 AS ok FROM assignments WHERE workspace_id = ? AND user_id = ? AND subject_kind = 'program' AND subject_id = ?
        AND responsibility = 'legal_reviewer' AND effective_from <= ? AND (effective_to IS NULL OR effective_to > ?) LIMIT 1`, [workspaceId, userId, aiReg.profile_id, iso, iso]);
    return r.results.length > 0;
}
//# sourceMappingURL=obligation-source-service.js.map